import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Trash2, Plus, Save, Loader2, ChevronUp, ChevronDown } from "lucide-react";

const inputCls =
  "px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors";

const TRACK_LABELS = {
  universities: "Universities",
  facilities: "Health Systems",
  lpn: "Vocational Nursing Schools",
  preceptors: "Preceptors",
  img: "Global USCE",
};

export const FormSetup = () => {
  const [configs, setConfigs] = useState([]);
  const [track, setTrack] = useState("universities");
  const [draft, setDraft] = useState(null);
  const [newField, setNewField] = useState({ label: "", type: "text", options: "" });
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/intake/forms").then(({ data }) => setConfigs(data));
  }, []);

  useEffect(() => {
    const cfg = configs.find((c) => c.track === track);
    if (cfg) setDraft(JSON.parse(JSON.stringify(cfg)));
    setStatus("");
  }, [track, configs]);

  if (!draft) return null;

  const updateField = (i, patch) => {
    const fields = [...draft.fields];
    fields[i] = { ...fields[i], ...patch };
    setDraft({ ...draft, fields });
  };

  const removeField = (i) => setDraft({ ...draft, fields: draft.fields.filter((_, idx) => idx !== i) });

  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= draft.fields.length) return;
    const fields = [...draft.fields];
    [fields[i], fields[j]] = [fields[j], fields[i]];
    setDraft({ ...draft, fields });
  };

  const addField = () => {
    if (!newField.label.trim()) return;
    const name = newField.label.trim().replace(/[^a-zA-Z0-9]+(.)/g, (_, c) => c.toUpperCase()).replace(/[^a-zA-Z0-9]/g, "");
    const f = { name: name.charAt(0).toLowerCase() + name.slice(1), label: newField.label.trim(), type: newField.type, required: false };
    if (newField.type === "select") f.options = newField.options.split(",").map((o) => o.trim()).filter(Boolean);
    setDraft({ ...draft, fields: [...draft.fields, f] });
    setNewField({ label: "", type: "text", options: "" });
  };

  const save = async () => {
    setBusy(true);
    setStatus("");
    try {
      const { data } = await api.put(`/intake/forms/${track}`, {
        fields: draft.fields,
        allow_upload: draft.allow_upload,
        upload_label: draft.upload_label,
      });
      setConfigs(configs.map((c) => (c.track === track ? data : c)));
      setStatus("Saved — live on the public intake form.");
    } catch (err) {
      setStatus(`Error: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6" data-testid="form-setup-panel">
      <div className="flex flex-wrap items-center gap-3">
        {Object.entries(TRACK_LABELS).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTrack(id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${track === id ? "bg-[#0B1B2B] text-[#D4AF37] border-[#0B1B2B]" : "border-[#E2E8F0] text-[#64748B] hover:border-[#D4AF37]"}`}
            data-testid={`form-setup-track-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5 bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0B1B2B]">
              {["Order", "Label", "Field Key", "Type", "Required", ""].map((h) => (
                <th key={h} className="text-left px-5 py-3 text-[#D4AF37] text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {draft.fields.map((f, i) => (
              <tr key={f.name} className="border-t border-[#E2E8F0]">
                <td className="px-5 py-2.5">
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveField(i, -1)} disabled={i === 0} className="p-1 rounded text-[#64748B] hover:text-[#0B1B2B] hover:bg-[#F8FAFC] disabled:opacity-30" data-testid={`form-field-up-${f.name}`} aria-label={`Move ${f.label} up`}>
                      <ChevronUp size={15} />
                    </button>
                    <button onClick={() => moveField(i, 1)} disabled={i === draft.fields.length - 1} className="p-1 rounded text-[#64748B] hover:text-[#0B1B2B] hover:bg-[#F8FAFC] disabled:opacity-30" data-testid={`form-field-down-${f.name}`} aria-label={`Move ${f.label} down`}>
                      <ChevronDown size={15} />
                    </button>
                  </div>
                </td>
                <td className="px-5 py-2.5">
                  <input className={`${inputCls} w-full`} value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} data-testid={`form-field-label-${f.name}`} />
                </td>
                <td className="px-5 py-2.5 text-[#64748B] font-mono text-xs">{f.name}</td>
                <td className="px-5 py-2.5 text-[#64748B]">{f.type}{f.options ? ` (${f.options.length} options)` : ""}</td>
                <td className="px-5 py-2.5">
                  <input type="checkbox" className="h-4 w-4 accent-[#D4AF37]" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} disabled={f.name === "email"} data-testid={`form-field-required-${f.name}`} />
                </td>
                <td className="px-5 py-2.5">
                  {f.name !== "email" && (
                    <button onClick={() => removeField(i)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`form-field-remove-${f.name}`} aria-label={`Remove ${f.label}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 p-4 bg-white border border-[#E2E8F0] rounded-xl shadow-xl">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Add field:</span>
        <input className={inputCls} placeholder="Field label" value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} data-testid="form-new-field-label" />
        <select className={inputCls} value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })} data-testid="form-new-field-type">
          {["text", "email", "tel", "select", "textarea", "date"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {newField.type === "select" && (
          <input className={`${inputCls} min-w-[220px]`} placeholder="Options (comma separated)" value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} data-testid="form-new-field-options" />
        )}
        <button onClick={addField} className="flex items-center gap-1.5 bg-[#0B1B2B] text-white text-xs font-semibold px-4 py-2 rounded-lg" data-testid="form-add-field-button">
          <Plus size={14} /> Add
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-5 p-4 bg-white border border-[#E2E8F0] rounded-xl shadow-xl">
        <label className="flex items-center gap-2 text-sm text-[#0B1B2B] font-medium">
          <input type="checkbox" className="h-4 w-4 accent-[#D4AF37]" checked={!!draft.allow_upload} onChange={(e) => setDraft({ ...draft, allow_upload: e.target.checked })} data-testid="form-allow-upload-toggle" />
          Enable encrypted document upload
        </label>
        {draft.allow_upload && (
          <input className={`${inputCls} min-w-[280px]`} value={draft.upload_label} onChange={(e) => setDraft({ ...draft, upload_label: e.target.value })} placeholder="Upload section label" data-testid="form-upload-label-input" />
        )}
        <button onClick={save} disabled={busy} className="ml-auto flex items-center gap-2 bg-[#D4AF37] text-[#0B1B2B] text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-[#e8c968] transition-colors disabled:opacity-60" data-testid="form-save-button">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          Publish Form
        </button>
        {status && <span className={`text-xs font-medium ${status.startsWith("Error") ? "text-red-600" : "text-emerald-700"}`} data-testid="form-save-status">{status}</span>}
      </div>
    </div>
  );
};
